import { datadog } from '@tv2-player/datadog-logger';
import { buildGUID } from './buildGUID';
import { INielsenCloudSdk } from './NielsenCloudSdk';
import {
  NielsenEventMetadata,
  NielsenEventTag,
  NielsenEventValue,
  NielsenGateway,
} from '../../types';

type AdMetadata = {
  type: 'ad' | 'preroll';
  assetid: string;
  stationId: string;
  islivestn: string;
  pbstarttm: string;
  subbrand: string;
};
type ContentMetadata = {
  type: 'content';
  assetid: string;
  program: string;
  title: string;
  length: string;
  airdate: string;
  isfullepisode: string;
  adloadtype: string;
  stationId: string;
  islivestn: string;
  pbstarttm: string;
  subbrand: string;
};
type Metadata = AdMetadata | ContentMetadata;

export type NielsenCloudPayload =
  | {
      metadata: {
        content: ContentMetadata;
        ad?: AdMetadata;
      };
      event: string;
      position: string;
      type: string;
      utc: string;
      sessid: string;
    }
  | {
      event: 'delete';
      utc: string;
    };

export class NielsenCloudGateway implements NielsenGateway {
  private _lastEmittedPlayhead: number | null = null;
  private _lastReceivedPlayhead: number | null = null;
  private _viewSessionId: string = buildGUID();

  private readonly _sdk: INielsenCloudSdk;

  constructor(sdk: INielsenCloudSdk) {
    this._sdk = sdk;
  }

  private _lastMetadata: Metadata | null = null;

  get lastMetadata(): AdMetadata | ContentMetadata | undefined {
    return this._lastMetadata ?? undefined;
  }

  set lastMetadata(value: Metadata) {
    this._lastMetadata = value;
    if (value.type === 'content') {
      this._lastContentMetadata = value;
      this._lastAdMetadata = null;
    } else {
      this._lastAdMetadata = value;
    }
  }

  private _lastContentMetadata: ContentMetadata | null = null;

  get lastContentMetadata(): ContentMetadata | undefined {
    return this._lastContentMetadata ?? undefined;
  }

  private _lastAdMetadata: AdMetadata | null = null;

  get lastAdMetadata(): AdMetadata | null {
    return this._lastAdMetadata;
  }

  emitPlayhead(value: number) {
    if (value === this._lastEmittedPlayhead) {
      return;
    }

    this._lastEmittedPlayhead = value;

    if (this.lastContentMetadata == null && this.lastAdMetadata?.type === 'preroll') {
      return;
    }

    if (this.lastContentMetadata == null) {
      datadog().error('NielsenCloudGateway: lastContentMetadata is null');
      return;
    }

    this._sdk.send({
      metadata: {
        content: this.lastContentMetadata,
        ...(this.lastAdMetadata
          ? {
              ad: this.lastAdMetadata,
            }
          : {}),
      },
      event: 'playhead',
      position: value.toString(),
      type: this.lastMetadata?.type === 'content' ? 'content' : 'ad',
      utc: Date.now().toString(),
      sessid: this._viewSessionId,
    });
  }

  send(
    tag: NielsenEventTag,
    value: NielsenEventValue,
    metadata?: NielsenEventMetadata | undefined
  ): void {
    if (tag === 'Stop') {
      this.emitPlayhead(value as number);
      this._lastReceivedPlayhead = null;
      this._lastEmittedPlayhead = null;
    }
    if (tag === 'End') {
      this.emitPlayhead(value as number);
      this._lastEmittedPlayhead = null;
      this._lastReceivedPlayhead = null;
      this._sdk.send({
        metadata: { content: this.lastContentMetadata! },
        event: 'complete',
        position: value.toString(),
        type: this.lastMetadata?.type === 'content' ? 'content' : 'ad',
        utc: Date.now().toString(),
        sessid: this._viewSessionId,
      });
    }
    if (tag === 'Load') {
      this.lastMetadata = value as Metadata;
      this._lastReceivedPlayhead = null;
      this._lastEmittedPlayhead = null;
    }
    if (tag === 'Playhead') {
      const timeSinceLastPlayheadEvent =
        this._lastEmittedPlayhead != null ? (value as number) - this._lastEmittedPlayhead : 0;
      if (this._lastEmittedPlayhead == null || timeSinceLastPlayheadEvent >= 10) {
        this.emitPlayhead(value as number);
      }
      this._lastReceivedPlayhead = value as number;
    }
  }
}
